import { useState } from "react";
import { Modal, Button, FormField, Select, Badge, ErrorState } from "@/ui";
import { useToast } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { useMintLien } from "../lib/api";
import { CAPS } from "../lib/caps";
import type { Id, LienCanal } from "../lib/types";
import { Mono } from "./Mono";

export interface LienInviteModalProps {
  open: boolean;
  onClose: () => void;
  dossierId: Id;
  patientLabel?: string;
}

/**
 * LienInviteModal — mint a patient connection-link (R-09, EB-03 lab-owned half). The raw claim code
 * is returned ONCE (hash-at-rest server-side); we surface it with a copy action and the "shown once"
 * warning, then it is unrecoverable.
 */
export function LienInviteModal({ open, onClose, dossierId, patientLabel }: LienInviteModalProps) {
  const { t } = useT();
  const toast = useToast();
  const canInvite = useCan(CAPS.lienInvite);
  const [canal, setCanal] = useState<LienCanal>("ailaji");
  const mint = useMintLien();
  const minted = mint.data;

  const submit = () => {
    mint.mutate(
      { dossier_id: dossierId, canal },
      { onSuccess: () => toast.success(t("reception.lien.minted")) },
    );
  };

  const copy = async () => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.claim_code);
      toast.success(t("reception.lien.copied"));
    } catch {
      toast.error(t("reception.error.generic"));
    }
  };

  const close = () => {
    mint.reset();
    onClose();
  };

  const canalOptions: Array<{ value: LienCanal; label: string }> = [
    { value: "ailaji", label: t("reception.lien.canal.ailaji") },
    { value: "portail", label: t("reception.lien.canal.portail") },
  ];

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("reception.lien.title")}
      footer={
        !minted ? (
          <>
            <Button variant="ghost" onClick={close}>
              {t("reception.form.cancel")}
            </Button>
            <Button onClick={submit} loading={mint.isPending} disabled={!canInvite}>
              {t("reception.lien.mint")}
            </Button>
          </>
        ) : (
          <Button onClick={close}>{t("action.close")}</Button>
        )
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        {patientLabel && <p style={{ margin: 0, color: token.slate }}>{patientLabel}</p>}

        {!minted ? (
          <FormField label={t("reception.lien.canal")}>
            {(p) => (
              <Select
                {...p}
                value={canal}
                onChange={(e) => setCanal(e.target.value as LienCanal)}
                options={canalOptions}
                disabled={!canInvite}
              />
            )}
          </FormField>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: token.s3 }}>
            <span style={{ fontSize: token.fsLabel }}>{t("reception.lien.code")}</span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: token.s3,
                padding: token.s3,
                background: token.surface2,
                border: `1px solid ${token.line}`,
                borderRadius: token.rSm,
              }}
            >
              <Mono className="nv-mono">
                <strong style={{ fontSize: token.fsBodyLg }}>{minted.claim_code}</strong>
              </Mono>
              <span style={{ flex: "1 1 auto" }} />
              <Button size="sm" variant="secondary" onClick={() => void copy()}>
                {t("reception.lien.copy")}
              </Button>
            </div>
            <p
              role="alert"
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: token.s2,
                color: token.warn,
                fontSize: token.fsCaption,
              }}
            >
              <Badge tone="warn">{t("reception.lien.canal." + minted.lien.canal)}</Badge>
              {t("reception.lien.warn")}
            </p>
          </div>
        )}

        {mint.isError && (
          <ErrorState message={mint.error?.message ?? t("reception.error.generic")} code={mint.error?.code} />
        )}
      </div>
    </Modal>
  );
}
